import styled from 'styled-components';
import {
  FigCaption,
  paletteHelper,
  spacingHelper,
  typographyHelper,
  mediaHelper,
} from '@property.com.au/ui-kit';
import type { FunctionComponent } from 'react';
import { Slide } from 'pure-react-carousel';
import { useMediaGalleryLightBoxContext } from '../../contexts/MediaGalleryLightBoxContext/MediaGalleryLightBoxContext';
import {
  isAerialImage,
  ListingMediaImage,
  AerialImage as AerialImageType,
} from '../../../app-kit/components/Image/types';
import { Image as _Image } from '../../../app-kit/components/Image/Image';
import metromapLogoUrl from '../../../assets/metromap.svg';

// Hardcoding these values allows us to calculate the maximum height for the image container
// Although not ideal, found this is the best solution to contain our dynamic image sizes for all viewports
const mobileActionBarHeight = 74;
const tabletActionBarHeight = 100; //Added SearchByImage button and some space below
const galleryTimelineHeight = 72;
const captionHeight = 52;
const mobileAdvertisementHeight = 148;

const mobileUIHeight =
  mobileActionBarHeight + galleryTimelineHeight + captionHeight + mobileAdvertisementHeight;
const tabletUIHeight = tabletActionBarHeight + galleryTimelineHeight + captionHeight;

const SlideContent = styled.figure`
  align-items: center;
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const ImageWrap = styled.div`
  display: flex;
  flex: 1;
  width: 100%;
  height: 100%;
  background: #0e0f0f;

  /* Fallback for browsers that do not support svh */
  max-height: calc(100vh - ${mobileUIHeight}px);
  ${mediaHelper('tabletMin')} {
    max-height: calc(100vh - ${tabletUIHeight}px);
  }
  /* stylelint-disable plugin/no-unsupported-browser-features */
  @supports (width: 100svh) {
    max-height: calc(100svh - ${mobileUIHeight}px);
    ${mediaHelper('tabletMin')} {
      max-height: calc(100svh - ${tabletUIHeight}px);
    }
  }
  /* stylelint-enable plugin/no-unsupported-browser-features */
`;

const AerialImage = styled.img`
  display: block;
  height: 100%;
  width: 100%;
  object-fit: contain;
  vertical-align: middle;
`;

const Caption = styled(FigCaption)`
  ${typographyHelper('body01')}
  padding: ${spacingHelper('medium')};
  color: ${paletteHelper('textInvertedStatic')};
  display: flex;
  gap: ${spacingHelper('medium')};
  align-items: end;
`;

const MetromapLogo = styled.img`
  height: 20px;
  align-self: center;
  vertical-align: unset;
`;

const ListingImage = styled(_Image)`
  display: block;
  height: 100%;
`;

export const MediaGallerySlide: FunctionComponent<{
  index: number;
  image: ListingMediaImage | AerialImageType;
}> = ({ index, image }) => {
  const { mediaItems, currentSlideIndex } = useMediaGalleryLightBoxContext();
  const currentMediaItem = mediaItems[currentSlideIndex];
  if (!currentMediaItem) return null;

  const eagerLoad = [currentSlideIndex - 1, currentSlideIndex, currentSlideIndex + 1].includes(
    index,
  );
  const loading = eagerLoad ? 'eager' : 'lazy';

  const { caption, alt } = image;
  const src = isAerialImage(image) ? image.uri : image.desktopFullScreenImageJpgUri;

  const tabPanelAccessibleId = `tabpanel-${currentSlideIndex}`;
  const ImageElement = isAerialImage(image) ? (
    <AerialImage src={src} loading={loading} alt={alt} />
  ) : (
    <ListingImage mediaItem={image} loading={loading} />
  );

  return (
    <Slide role="tabpanel" id={tabPanelAccessibleId} index={index} tabIndex={-1}>
      <SlideContent>
        <ImageWrap>{ImageElement}</ImageWrap>
        <Caption>
          {caption}
          {isAerialImage(image) && <MetromapLogo alt="metromap-logo" src={metromapLogoUrl} />}
        </Caption>
      </SlideContent>
    </Slide>
  );
};
